import { useSubCategoriesQuery } from "~/api/categories";
import { getGroupedCategories } from "~/helpers/getGroupedCategories";
import { CurrentStepProps } from "../../getSideMenuItems";
import { StrapiSubCategory } from "~/shared-types";
import { useEffect } from "react";
import { Select } from "~/components/Backroom/Select";
import { useWithFetchAll } from "~/api/useWithFetchAll";
import extractMainOrSubCategoriIdFromSelectValue, {
  CategoryType,
} from "~/helpers/extractMainOrSubCategoryIdFromSelectValue";

export const formatSubCategoryOptions = (categories: StrapiSubCategory[]) => {
  const groupedCategories = getGroupedCategories(categories);

  return groupedCategories.map(({ mainCategory, subCategories }) => ({
    label: mainCategory.attributes.name,
    options: subCategories.map((subCategory) => ({
      label: subCategory.attributes.name,
      value: `${subCategory.id}-${mainCategory.id}`,
    })),
  }));
};

export const SubCategorySelect = ({ form }: CurrentStepProps) => {
  const { control, watch, setValue } = form;
  const { data, fetchAllPages, isFetching } = useWithFetchAll(
    useSubCategoriesQuery()
  );
  const subCategories = data?.pages
    ?.flatMap((page) => page?.data)
    .filter(Boolean) as StrapiSubCategory[];

  const selectedSubCategory = watch("subCategory");

  // Effect: Set mainCategory when subCategory is selected
  useEffect(() => {
    if (selectedSubCategory) {
      const subCategoryId = extractMainOrSubCategoriIdFromSelectValue(
        selectedSubCategory.value as string,
        CategoryType.SUB
      );

      const matchingSubCategory = subCategories.find(
        (subCategory) => subCategory.id === subCategoryId
      );
      const mainCategory = matchingSubCategory?.attributes.mainCategory?.data;

      mainCategory &&
        setValue("mainCategory", {
          label: mainCategory.attributes.name,
          value: mainCategory.id,
        });
    }
  }, [selectedSubCategory?.value]);

  // Effect: Fetch all sub categories immediately
  useEffect(() => {
    fetchAllPages();
  }, []);

  const subCategoryOptions = formatSubCategoryOptions(subCategories);

  return (
    <Select
      control={control}
      id="subCategory"
      label="Vælg en kategori:"
      validation={{ required: "Vælg venligst en kategori" }}
      className="mb-12"
      isLoading={isFetching}
      options={subCategoryOptions}
    />
  );
};
